import { groupBy, sumBy } from 'lodash';
import moment from 'moment';

// @flow
/**
 * @public
 */
export type FilterType = 'Year' | 'Month' | 'Week' | 'Day';

export type SourceDataType = {
  day: string;
  seriesName: string;
  [key: string]: any;
};

export type SeriesData = {
  name: string;
  values: number[];
};

export type FilterData = {
  timeline: string[];
  series: SeriesData[];
};

const generateFullTimeline = (timeType: FilterType, start: moment.Moment, end: moment.Moment): string[] => {
  const timeline: string[] = [];
  const current = start.clone();

  while (current.isSameOrBefore(end)) {
    let key: string;
    switch (timeType) {
      case 'Year':
        key = current.year().toString();
        current.add(1, 'year');
        break;
      case 'Month':
        key = `${current.year()}-Month${current.month() + 1}`;
        current.add(1, 'month');
        break;
      case 'Week':
        key = `${current.isoWeekYear()}-Week${current.isoWeek()}`;
        current.add(1, 'week');
        break;
      case 'Day':
        key = current.format('YYYY-MM-DD');
        current.add(1, 'day');
        break;
      default:
        throw new Error('Invalid timeType');
    }
    timeline.push(key);
  }
  return timeline;
};

export const filterDataByDate = (timeType: FilterType, sourceData: SourceDataType[]): FilterData => {
  const fullSourceData = sourceData.map((item) => ({
    ...item,
    day: moment(item.day).format('YYYY-MM-DD'),
    week: moment(item.day).isoWeek(),
    weekYear: moment(item.day).isoWeekYear(),
    month: new Date(item.day).getMonth() + 1,
    year: new Date(item.day).getFullYear(),
  }));

  if (fullSourceData.length === 0) {
    return { timeline: [], series: [] };
  }
  const allDates = fullSourceData.map((item) => moment(item.day));
  const earliestDate = moment.min(allDates);
  const latestDate = moment.max(allDates);

  const timeline = generateFullTimeline(timeType, earliestDate, latestDate);

  const getMapping = (list: typeof fullSourceData) => {
    const map: Record<string, typeof fullSourceData> = {};
    list.forEach((element) => {
      let key: string;
      if (timeType === 'Week') {
        key = `${element.weekYear}-Week${element.week}`;
      } else if (timeType === 'Month') {
        key = `${element.year}-Month${element.month}`;
      } else if (timeType === 'Year') {
        key = element.year.toString();
      } else {
        // Day
        key = element.day;
      }
      map[key] = map[key] || [];
      map[key].push(element);
    });
    return map;
  };

  const getValues = (timeline: string[], object: ReturnType<typeof getMapping>) => {
    return timeline.map((key) => {
      const values = object[key];
      if (!values || values.length === 0) {
        return 0;
      }
      if (timeType !== 'Day') {
        const group = groupBy(values, 'day');
        const maxElement = Object.values(group).reduce((pre, cur) => {
          const preSum = sumBy(pre, 'value');
          const curSum = sumBy(cur, 'value');
          return preSum > curSum ? pre : cur;
        }, []);
        return sumBy(maxElement, 'value');
      } else {
        return sumBy(values, 'value') || 0;
      }
    });
  };

  const seriesNames = Array.from(new Set(fullSourceData.map((item) => item.seriesName)));
  const result = seriesNames.map((name) => {
    const filteredData = fullSourceData.filter((item) => item.seriesName === name);
    const mapping = getMapping(filteredData);
    return { name, values: getValues(timeline, mapping) };
  });

  return { timeline, series: result };
};
